import React, { useState, useEffect, useRef } from "react";
import { Bell, PlusCircle, X, Check, AlertTriangle, ChevronDown } from "lucide-react";
const customCheckboxStyles = `
  .custom-checkbox {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid #d1d5db;
    border-radius: 3px;
    background-color: white;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
  } 
  .custom-checkbox:checked {
    background-color: #003366;
    border-color: #003366;
  }  
  .custom-checkbox:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }  
  .custom-checkbox:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 51, 102, 0.2);
  } 
  .custom-checkbox:hover:not(:checked) {
    border-color: #003366;
    background-color: rgba(0, 51, 102, 0.05);
  }
`;
const CustomSelect = ({ value, onChange, options, placeholder, disabled, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
    setHighlightedIndex(-1);
  };
  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }
    const actions = {
      'Escape': () => { setIsOpen(false); setHighlightedIndex(-1); },
      'ArrowDown': () => setHighlightedIndex(prev => Math.min(prev + 1, options.length - 1)),
      'ArrowUp': () => setHighlightedIndex(prev => Math.max(prev - 1, 0)),
      'Enter': () => highlightedIndex >= 0 && handleSelect(options[highlightedIndex].value)
    };
    if (actions[e.key]) {
      e.preventDefault();
      actions[e.key]();
    }
  };
  const selectedOption = options.find(opt => opt.value === value);
  const baseClasses = "w-full p-2.5 text-sm border rounded-md bg-white cursor-pointer flex items-center justify-between transition-all duration-200 focus:outline-none";
  const stateClasses = disabled
    ? 'text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50'
    : `text-[#003366] border-gray-200 hover:border-[#003366] ${isOpen ? 'border-[#003366] ring-1 ring-[#003366]/20' : ''}`;
  return (
    <div className="relative" ref={selectRef}>
      <div
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`${baseClasses} ${stateClasses} ${className || ''}`}
      >
        <span className={selectedOption ? 'text-[#003366]' : 'text-gray-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-500 transition-transform duration-200 ${isOpen && !disabled ? 'rotate-180' : ''}`}
        />
      </div>
      {isOpen && !disabled && (
  <div className="absolute z-[9999] w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
    style={{
      top: selectRef.current?.getBoundingClientRect().bottom + window.scrollY + 4 > window.innerHeight - 200 ? 'auto' : '100%',
      bottom: selectRef.current?.getBoundingClientRect().bottom + window.scrollY + 4 > window.innerHeight - 200 ? '100%' : 'auto',
      marginTop: selectRef.current?.getBoundingClientRect().bottom + window.scrollY + 4 > window.innerHeight - 200 ? '-4px' : '4px'
    }}>
          {options.map((option, index) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 text-xs cursor-pointer transition-colors duration-150 ${
                highlightedIndex === index || value === option.value
                  ? 'bg-[#003366]/10 text-[#003366]'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-[#003366]'
              }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default function AlarmConfig({
  alarmConfig,
  updateAlarmConfig,
  metricName,
  resourceNames = [],
  validationErrors = {},
  clearValidationError
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [resourceAlarmMap, setResourceAlarmMap] = useState({});
  const [duplicateWarnings, setDuplicateWarnings] = useState({});
  const [resourceValidationErrors, setResourceValidationErrors] = useState({});
  const alarms = Array.isArray(alarmConfig.alarms) ? alarmConfig.alarms : [];
  // Create resource-to-alarm mapping and check for duplicates
  useEffect(() => {
    const newResourceAlarmMap = {};
    const newDuplicateWarnings = {};
    alarmConfig.alarms?.forEach((alarm, index) => {
      const resources = alarm.resourceNames || (alarm.resourceName ? [alarm.resourceName] : []);
      resources.forEach(resourceName => {
        if (!newResourceAlarmMap[resourceName]) newResourceAlarmMap[resourceName] = [];
        newResourceAlarmMap[resourceName].push(index);
        if (newResourceAlarmMap[resourceName].length > 1) {
          newDuplicateWarnings[resourceName] = true;
        }
      });
    });
    setResourceAlarmMap(newResourceAlarmMap);
    setDuplicateWarnings(newDuplicateWarnings);
  }, [alarmConfig]);
  // Clean up alarms when resources are no longer available
  useEffect(() => {
    const updatedAlarms = alarms.map(alarm => {
      if (alarm.resourceNames?.length) {
        const validResourceNames = alarm.resourceNames.filter(name => resourceNames.includes(name));
        return validResourceNames.length !== alarm.resourceNames.length 
          ? { ...alarm, resourceNames: validResourceNames }
          : alarm;
      }
      return alarm;
    }).filter(alarm => alarm.resourceNames?.length > 0);
    if (updatedAlarms.length !== alarms.length || JSON.stringify(updatedAlarms) !== JSON.stringify(alarms)) {
      if (updatedAlarms.length === 0) {
        updateAlarmConfig(metricName, "enabled", false);
        updateAlarmConfig(metricName, "alarms", []);
        setIsExpanded(false);
      } else {
        updateAlarmConfig(metricName, "alarms", updatedAlarms);
      }
    }
  }, [resourceNames]);
  const hasExistingAlarm = (resourceName, currentAlarmIndex) => {
    return (resourceAlarmMap[resourceName] || []).some(index => index !== currentAlarmIndex);
  };
  const isOriginalAlarmForResource = (resourceName, currentAlarmIndex) => {
    const alarmIndexes = resourceAlarmMap[resourceName] || [];
    return alarmIndexes.length > 0 && alarmIndexes[0] === currentAlarmIndex;
  };
  const handleFieldChange = (field, value, alarmIndex = 0) => {
    const updatedAlarms = [...alarms];
    updatedAlarms[alarmIndex] = { ...updatedAlarms[alarmIndex], [field]: value };
    updateAlarmConfig(metricName, "alarms", updatedAlarms);
    clearValidationError?.(metricName, alarmIndex, field);
  };
  const handleResourceSelectionChange = (resourceName, alarmIndex = 0) => {
    const updatedAlarms = [...alarms];
    const currentAlarm = updatedAlarms[alarmIndex];
    const currentResourceNames = currentAlarm.resourceNames || [];
    let newResourceNames;
    if (resourceName === "") {
      // "All Resources" checkbox
      if (isAllResourcesSelected(alarmIndex)) {
        handleRemoveAlarm(alarmIndex);
        return;
      } else {
        newResourceNames = [...resourceNames];
      }
    } else {
      if (!resourceNames.includes(resourceName)) return;
      
      if (currentResourceNames.includes(resourceName)) {
        if (currentResourceNames.length === 1) {
          setResourceValidationErrors(prev => ({
            ...prev,
            [alarmIndex]: "At least one resource must be selected for the alarm. To remove this alarm, use the X button."
          }));
          return;
        }
        newResourceNames = currentResourceNames.filter(name => name !== resourceName);
      } else {
        newResourceNames = [...currentResourceNames, resourceName];
      }
    }
    setResourceValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[alarmIndex];
      return updated;
    });
    updatedAlarms[alarmIndex] = { ...currentAlarm, resourceNames: newResourceNames };
    updateAlarmConfig(metricName, "alarms", updatedAlarms);
  };
  const handleAddAlarm = () => {
    if (!alarmConfig.enabled) updateAlarmConfig(metricName, "enabled", true);  
    if (alarms.length === 0) {
      updateAlarmConfig(metricName, "alarms", [{
        statistic: "", comparisonOperator: "", threshold: "", resourceNames: [], alarmName: ""
      }]);
    }
    setIsExpanded(true);
  };
  const handleRemoveAlarm = (index) => {
    const updatedAlarms = alarms.filter((_, i) => i !== index);
    setResourceValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[index];
      const reindexed = {};
      Object.keys(updated).forEach(key => {
        const oldIndex = parseInt(key);
        reindexed[oldIndex > index ? oldIndex - 1 : key] = updated[key];
      });
      return reindexed;
    });
    if (updatedAlarms.length === 0) {
      updateAlarmConfig(metricName, "enabled", false);
      updateAlarmConfig(metricName, "alarms", []);
      setIsExpanded(false);
    } else {
      updateAlarmConfig(metricName, "alarms", updatedAlarms);
    }
  };
  const handleAddAnotherAlarm = () => {
    updateAlarmConfig(metricName, "alarms", [...alarms, {
      statistic: "", comparisonOperator: "", threshold: "", resourceNames: [], alarmName: ""
    }]);
  };
  const isResourceSelected = (resourceName, alarmIndex) => {
    const alarm = alarms[alarmIndex];
    return alarm.resourceNames?.includes(resourceName) || alarm.resourceName === resourceName;
  };
  const isAllResourcesSelected = (alarmIndex) => {
    const alarm = alarms[alarmIndex];
    return alarm.resourceNames?.length > 0 && resourceNames.length > 0 && 
           resourceNames.every(name => alarm.resourceNames.includes(name));
  };
  const getAlarmFieldError = (alarmIndex, fieldName) => {
    return validationErrors.alarms?.[alarmIndex]?.[fieldName];
  };
  const selectOptions = {
    statistic: [
      { value: "Average", label: "Average" },
      { value: "Maximum", label: "Maximum" },
      { value: "Minimum", label: "Minimum" },
      { value: "Sum", label: "Sum" },
      { value: "SampleCount", label: "Count" }
    ],
    comparisonOperator: [
      { value: "GreaterThanOrEqualToThreshold", label: "≥ Greater than or equal" },
      { value: "GreaterThanThreshold", label: "> Greater than" },
      { value: "LessThanThreshold", label: "< Less than" },
      { value: "LessThanOrEqualToThreshold", label: "≤ Less than or equal" }
    ]
  };
  return (
    <div className="ml-4 mt-1 mb-2">
      <style>{customCheckboxStyles}</style>
      <div className="flex items-center">
        <button
  onClick={handleAddAlarm}
  className="flex items-center text-xs py-0.5 px-1.5 rounded transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
>
  <Bell size={12} className="mr-1" />
  {alarms.length > 0 ? "Edit Alarm" : "Add Alarm"}
</button>
      </div>
      {isExpanded && (
        <div className="mt-1.5 space-y-1.5">
          <div className="mb-1">
            <span className="text-xs font-medium text-[#003366]">Alarm Configuration</span>
          </div>
          {alarms.map((alarm, index) => (
            <div key={index} className="bg-white p-3 rounded border border-gray-100 relative pr-6">
              <button
                onClick={() => handleRemoveAlarm(index)}
                className="absolute top-1.5 right-1.5 p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Remove this alarm"
              >
                <X size={12} />
              </button>
              <div className="grid grid-cols-1 gap-2 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Statistic</label>
                  <CustomSelect
                    value={alarm.statistic}
                    onChange={(e) => handleFieldChange('statistic', e.target.value, index)}
                    options={selectOptions.statistic}
                    placeholder="Select statistic"
                    className={`text-xs ${getAlarmFieldError(index, 'statistic') ? 'border-red-500' : ''}`}
                  />
                  {getAlarmFieldError(index, 'statistic') && (
                    <p className="mt-1 text-xs text-red-600">Statistic is required</p>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Resource Names</label>
                <div className="border-0 rounded max-h-32 overflow-y-auto bg-gray-50">
                  <div className="p-2 border-b border-gray-200 bg-white rounded-t">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAllResourcesSelected(index)}
                        onChange={() => handleResourceSelectionChange("", index)}
                        className="custom-checkbox"
                      />
                      <span className="ml-2 text-xs text-gray-700 font-medium">All Resources</span>
                      {isAllResourcesSelected(index) && <Check size={12} className="ml-auto text-[#003366]" />}
                    </label>
                  </div>
                  {resourceNames.map((resourceName) => {
                    const hasExisting = hasExistingAlarm(resourceName, index);
                    const isSelected = isResourceSelected(resourceName, index);
                    const isOriginalAlarm = isOriginalAlarmForResource(resourceName, index);
                    return (
                      <div key={resourceName} className="border-b border-gray-200 last:border-b-0 last:rounded-b bg-white">
                        <div className={`p-2 ${isOriginalAlarm && isSelected ? 'bg-blue-50 border-l-2 border-l-[#003366]' : ''}`}>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleResourceSelectionChange(resourceName, index)}
                              className="custom-checkbox"
                            />
                            <span className="ml-2 text-xs text-gray-700 flex-1">{resourceName}</span>
                            {isSelected && <Check size={12} className="text-[#003366]" />}
                          </label>
                        </div>
                        {hasExisting && isSelected && !isOriginalAlarm && (
                          <div className="px-2 pb-2">
                            <div className="bg-amber-50 border border-amber-200 rounded p-2">
                              <div className="flex items-start">
                                <AlertTriangle size={12} className="text-amber-500 mt-0.5 mr-1.5 flex-shrink-0" />
                                <div className="text-xs text-amber-700">
                                  <div className="font-medium">Alarm already exists for this resource</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {resourceNames.length === 0 && (
                    <div className="p-3 text-xs text-gray-500 bg-white rounded-b">No resources available</div>
                  )}
                </div>
              </div>
              {resourceValidationErrors[index] && (
                <div className="mb-3">
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <div className="flex items-start">
                      <AlertTriangle size={12} className="text-red-500 mt-0.5 mr-1.5 flex-shrink-0" />
                      <div className="text-xs text-red-700">{resourceValidationErrors[index]}</div>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Comparison</label>
                  <CustomSelect
                    value={alarm.comparisonOperator}
                    onChange={(e) => handleFieldChange('comparisonOperator', e.target.value, index)}
                    options={selectOptions.comparisonOperator}
                    placeholder="Select comparison"
                    className={`text-xs ${getAlarmFieldError(index, 'comparisonOperator') ? 'border-red-500' : ''}`}
                  />
                  {getAlarmFieldError(index, 'comparisonOperator') && (
                    <p className="mt-1 text-xs text-red-600">Comparison operator is required</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Threshold</label>
                  <input
                    type="number"
                    value={alarm.threshold}
                    onChange={(e) => handleFieldChange('threshold', e.target.value, index)}
                    className={`w-full p-2 text-xs border-0 rounded bg-gray-50 focus:bg-white focus:ring-1 focus:ring-[#003366] focus:ring-opacity-20 transition-all ${
                      getAlarmFieldError(index, 'threshold') ? 'ring-1 ring-red-500 ring-opacity-20 bg-red-50' : ''
                    }`}
                    placeholder="Enter threshold value"
                  />
                  {getAlarmFieldError(index, 'threshold') && (
                    <p className="mt-1 text-xs text-red-600">Threshold is required</p>
                  )}
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={handleAddAnotherAlarm}
            className="flex items-center text-xs text-[#003366] hover:text-[#004488] mt-1.5 font-medium"
          >
            <PlusCircle size={12} className="mr-1.5" />
            Add another alarm for this metric
          </button>
        </div>
      )}
    </div>
  );
}